import React, { useEffect, useState, useCallback } from 'react';
import { MainLayout } from '@/components/Layout';
import { crmApi } from '@/lib/api';
import { 
  Users, TrendingUp, AlertTriangle, Clock, MessageSquare, XCircle,
  Star, ArrowRight, Send, Tag, Shield, Filter, Trash2, Eye, RefreshCw
} from 'lucide-react';

// ===================== DASHBOARD TAB =====================
function DashboardTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmApi.dashboard(days);
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="p-8 text-center text-slate-500" data-testid="crm-loading">Carregando dashboard...</div>;

  const kpis = [
    { label: 'Total Clientes', value: data.total_customers, icon: Users, color: 'bg-blue-500' },
    { label: 'Novos (periodo)', value: data.new_customers, icon: TrendingUp, color: 'bg-green-500' },
    { label: 'Ativos', value: data.active_customers, icon: Star, color: 'bg-purple-500' },
    { label: 'Em Risco', value: data.at_risk_customers, icon: AlertTriangle, color: 'bg-amber-500' },
    { label: 'Ticket Medio', value: `R$ ${data.avg_ticket.toFixed(2)}`, icon: TrendingUp, color: 'bg-teal-500' },
    { label: 'Taxa Recompra', value: `${data.repurchase_rate}%`, icon: RefreshCw, color: 'bg-indigo-500' },
    { label: 'LTV 90d', value: `R$ ${data.ltv_90d.toFixed(2)}`, icon: TrendingUp, color: 'bg-pink-500' },
    { label: 'Cancelamentos', value: data.cancellations_period, icon: XCircle, color: 'bg-red-500' },
  ];

  return (
    <div data-testid="crm-dashboard">
      <div className="flex items-center gap-3 mb-6">
        <select data-testid="crm-period-select" value={days} onChange={e => setDays(Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm">
          <option value={7}>7 dias</option>
          <option value={30}>30 dias</option>
          <option value={60}>60 dias</option>
          <option value={90}>90 dias</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <div key={i} className="bg-white rounded-xl border p-4 shadow-sm" data-testid={`kpi-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`${kpi.color} p-2 rounded-lg`}>
                  <Icon size={16} className="text-white" />
                </div>
                <span className="text-xs text-slate-500">{kpi.label}</span>
              </div>
              <p className="text-xl font-bold text-slate-800">{kpi.value}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4">Top Clientes por Receita</h3>
          {data.top_customers.length === 0 ? (
            <p className="text-sm text-slate-400">Nenhum dado</p>
          ) : (
            <div className="space-y-3">
              {data.top_customers.map((c, i) => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0" data-testid={`top-customer-${i}`}>
                  <div>
                    <p className="font-medium text-sm">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.order_count} pedidos</p>
                  </div>
                  <span className="font-bold text-green-600 text-sm">R$ {c.total_spent.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-4">Receita por Canal</h3>
          {data.by_origin.length === 0 ? (
            <p className="text-sm text-slate-400">Nenhum dado</p>
          ) : (
            <div className="space-y-3">
              {data.by_origin.map((o, i) => {
                const maxRev = Math.max(...data.by_origin.map(x => x.revenue));
                const pct = maxRev > 0 ? (o.revenue / maxRev * 100) : 0;
                return (
                  <div key={i} data-testid={`origin-${i}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{o.origin}</span>
                      <span className="text-slate-500">{o.count} pedidos - R$ {o.revenue.toFixed(2)}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div className="bg-gradient-to-r from-[#FF6B6B] to-[#FF7A5A] h-2 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-2">Segmentos</h3>
          <p className="text-3xl font-bold text-indigo-600">{data.segments_count}</p>
          <p className="text-xs text-slate-400 mt-1">segmentos ativos</p>
        </div>
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="font-semibold text-slate-800 mb-2">Mensagens Hoje</h3>
          <p className="text-3xl font-bold text-teal-600">{data.messages_today}</p>
          <p className="text-xs text-slate-400 mt-1">mensagens enviadas</p>
        </div>
      </div>
    </div>
  );
}

// ===================== CUSTOMERS TAB =====================
function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('monetary');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmApi.customers({ search: search || undefined, sort_by: sortBy, limit: 100 });
      setCustomers(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search, sortBy]);

  useEffect(() => { load(); }, [load]);

  const loadProfile = async (id) => {
    try {
      const res = await crmApi.customerProfile(id);
      setProfile(res.data);
      setSelected(id);
    } catch (e) { console.error(e); }
  };

  const classColors = {
    VIP: 'bg-yellow-100 text-yellow-800',
    RECORRENTE: 'bg-blue-100 text-blue-800',
    NOVO: 'bg-green-100 text-green-800',
    EM_RISCO: 'bg-red-100 text-red-800',
    INATIVO: 'bg-slate-100 text-slate-600',
    REGULAR: 'bg-slate-50 text-slate-600',
  };

  return (
    <div data-testid="crm-customers">
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          data-testid="crm-search"
          type="text"
          placeholder="Buscar por nome, telefone ou email..."
          className="flex-1 border rounded-lg px-4 py-2 text-sm"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select data-testid="crm-sort" value={sortBy} onChange={e => setSortBy(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="monetary">Maior Receita</option>
          <option value="frequency">Maior Frequencia</option>
          <option value="recency">Mais Recentes</option>
          <option value="name">Nome A-Z</option>
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={selected ? 'lg:col-span-1' : 'lg:col-span-3'}>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Cliente</th>
                    {!selected && <th className="px-4 py-3 text-left">Telefone</th>}
                    <th className="px-4 py-3 text-center">Freq.</th>
                    <th className="px-4 py-3 text-right">Receita</th>
                    <th className="px-4 py-3 text-center">Classe</th>
                    <th className="px-4 py-3 text-center">Acao</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">Carregando...</td></tr>
                  ) : customers.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-slate-400">Nenhum cliente encontrado</td></tr>
                  ) : customers.map((c, i) => (
                    <tr key={c.id} className={`border-t hover:bg-slate-50 ${selected === c.id ? 'bg-orange-50' : ''}`} data-testid={`customer-row-${i}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium">{c.name}</p>
                        <div className="flex gap-1 mt-1">
                          {c.tags.map(t => (
                            <span key={t} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{t}</span>
                          ))}
                        </div>
                      </td>
                      {!selected && <td className="px-4 py-3 text-slate-500">{c.phone}</td>}
                      <td className="px-4 py-3 text-center font-medium">{c.frequency}</td>
                      <td className="px-4 py-3 text-right font-medium text-green-700">R$ {c.monetary.toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${classColors[c.classification] || ''}`}>
                          {c.classification}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => loadProfile(c.id)} className="text-[#FF6B6B] hover:text-[#FF7A5A]" data-testid={`view-profile-${i}`}>
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {selected && profile && (
          <div className="lg:col-span-2" data-testid="customer-profile-panel">
            <div className="bg-white rounded-xl border p-5 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">{profile.name}</h3>
                  <p className="text-sm text-slate-500">{profile.phone} {profile.email && `| ${profile.email}`}</p>
                  <div className="flex gap-2 mt-2">
                    {profile.tags.map(t => (
                      <span key={t} className="text-xs bg-[#FF6B6B] text-white px-2 py-1 rounded-full">{t}</span>
                    ))}
                    <span className={`text-xs px-2 py-1 rounded-full ${profile.lgpd_consent ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {profile.lgpd_consent ? 'LGPD OK' : 'Sem Consentimento'}
                    </span>
                  </div>
                </div>
                <button onClick={() => { setSelected(null); setProfile(null); }} className="text-slate-400 hover:text-slate-600">
                  <XCircle size={20} />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Recencia</p>
                  <p className="text-lg font-bold">{profile.rfm.recency_days ?? '-'} dias</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Frequencia</p>
                  <p className="text-lg font-bold">{profile.rfm.frequency}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Receita Total</p>
                  <p className="text-lg font-bold">R$ {profile.rfm.monetary.toFixed(2)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500">Ticket Medio</p>
                  <p className="text-lg font-bold">R$ {profile.rfm.avg_ticket.toFixed(2)}</p>
                </div>
              </div>

              {profile.top_products.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Produtos Favoritos</h4>
                  <div className="space-y-2">
                    {profile.top_products.map((p, i) => (
                      <div key={i} className="flex justify-between items-center py-1 border-b last:border-0 text-sm">
                        <span>{p.product_name}</span>
                        <span className="text-slate-500">{p.total_qty}x - R$ {p.total_value.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {profile.by_origin.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Canais de Compra</h4>
                  <div className="flex gap-2">
                    {profile.by_origin.map((o, i) => (
                      <span key={i} className="text-xs bg-slate-100 px-3 py-1.5 rounded-full">{o.origin}: {o.count}</span>
                    ))}
                  </div>
                </div>
              )}

              {profile.order_history.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Historico Mensal</h4>
                  <div className="space-y-2">
                    {profile.order_history.map((h, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                        <span className="text-slate-500">{h.month}</span>
                        <span>{h.orders} pedidos - R$ {h.revenue.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ===================== SEGMENTS TAB =====================
function SegmentsTab() {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', min_frequency: '', min_monetary: '', max_recency_days: '', min_recency_days: '' });
  const [segmentDetail, setSegmentDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmApi.segments();
      setSegments(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const filters = {};
    if (form.min_frequency) filters.min_frequency = Number(form.min_frequency);
    if (form.min_monetary) filters.min_monetary = Number(form.min_monetary);
    if (form.max_recency_days) filters.max_recency_days = Number(form.max_recency_days);
    if (form.min_recency_days) filters.min_recency_days = Number(form.min_recency_days);
    try {
      await crmApi.createSegment({ name: form.name, description: form.description, filters });
      setShowCreate(false);
      setForm({ name: '', description: '', min_frequency: '', min_monetary: '', max_recency_days: '', min_recency_days: '' });
      load();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir segmento?')) return;
    try { await crmApi.deleteSegment(id); load(); } catch (e) { console.error(e); }
  };

  const viewCustomers = async (id) => {
    try {
      const res = await crmApi.segmentCustomers(id);
      setSegmentDetail(res.data);
    } catch (e) { console.error(e); }
  };

  return (
    <div data-testid="crm-segments">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-semibold text-slate-800">Segmentos de Clientes</h3>
        <button data-testid="create-segment-btn" onClick={() => setShowCreate(!showCreate)} className="bg-[#FF6B6B] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#FF7A5A] flex items-center gap-2">
          <Filter size={14} /> Novo Segmento
        </button>
      </div>

      {showCreate && (
        <div className="bg-white rounded-xl border p-5 shadow-sm mb-6" data-testid="create-segment-form">
          <h4 className="font-semibold mb-3">Criar Segmento</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <input data-testid="segment-name" placeholder="Nome do segmento" className="border rounded-lg px-3 py-2 text-sm" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <input data-testid="segment-desc" placeholder="Descricao" className="border rounded-lg px-3 py-2 text-sm" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            <input data-testid="segment-min-freq" type="number" placeholder="Freq. minima" className="border rounded-lg px-3 py-2 text-sm" value={form.min_frequency} onChange={e => setForm({ ...form, min_frequency: e.target.value })} />
            <input data-testid="segment-min-monetary" type="number" placeholder="Receita minima (R$)" className="border rounded-lg px-3 py-2 text-sm" value={form.min_monetary} onChange={e => setForm({ ...form, min_monetary: e.target.value })} />
            <input data-testid="segment-max-recency" type="number" placeholder="Recencia max (dias)" className="border rounded-lg px-3 py-2 text-sm" value={form.max_recency_days} onChange={e => setForm({ ...form, max_recency_days: e.target.value })} />
            <input data-testid="segment-min-recency" type="number" placeholder="Recencia min (dias)" className="border rounded-lg px-3 py-2 text-sm" value={form.min_recency_days} onChange={e => setForm({ ...form, min_recency_days: e.target.value })} />
          </div>
          <button data-testid="save-segment-btn" onClick={handleCreate} disabled={!form.name} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
            Criar Segmento
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {loading ? (
          <p className="text-slate-400 col-span-3 text-center py-8">Carregando...</p>
        ) : segments.length === 0 ? (
          <p className="text-slate-400 col-span-3 text-center py-8">Nenhum segmento criado</p>
        ) : segments.map((s, i) => (
          <div key={s.id} className="bg-white rounded-xl border p-4 shadow-sm" data-testid={`segment-card-${i}`}>
            <div className="flex justify-between items-start">
              <div>
                <h4 className="font-semibold text-slate-800">{s.name}</h4>
                <p className="text-xs text-slate-500 mt-1">{s.description || 'Sem descricao'}</p>
              </div>
              <button onClick={() => handleDelete(s.id)} className="text-slate-400 hover:text-red-500">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="flex justify-between items-center mt-3">
              <span className="text-lg font-bold text-[#FF6B6B]">{s.customer_count} clientes</span>
              <button onClick={() => viewCustomers(s.id)} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                Ver <ArrowRight size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {segmentDetail && (
        <div className="bg-white rounded-xl border p-5 shadow-sm" data-testid="segment-detail">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">{segmentDetail.segment.name} - {segmentDetail.customers.length} clientes</h4>
            <button onClick={() => setSegmentDetail(null)} className="text-slate-400 hover:text-slate-600">
              <XCircle size={18} />
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Nome</th>
                <th className="px-4 py-2 text-left">Telefone</th>
                <th className="px-4 py-2 text-center">Freq.</th>
                <th className="px-4 py-2 text-right">Receita</th>
                <th className="px-4 py-2 text-center">Recencia</th>
              </tr>
            </thead>
            <tbody>
              {segmentDetail.customers.map((c, i) => (
                <tr key={c.id} className="border-t">
                  <td className="px-4 py-2">{c.name}</td>
                  <td className="px-4 py-2 text-slate-500">{c.phone}</td>
                  <td className="px-4 py-2 text-center">{c.frequency}</td>
                  <td className="px-4 py-2 text-right text-green-700">R$ {c.monetary.toFixed(2)}</td>
                  <td className="px-4 py-2 text-center">{c.recency_days ?? '-'} dias</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===================== MESSAGES TAB =====================
function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [msgRes, tmplRes] = await Promise.all([crmApi.messages(100), crmApi.templates()]);
      setMessages(msgRes.data);
      setTemplates(tmplRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusColors = {
    PENDENTE: 'bg-yellow-100 text-yellow-800',
    ENVIADO: 'bg-blue-100 text-blue-800',
    ENTREGUE: 'bg-green-100 text-green-800',
    ERRO: 'bg-red-100 text-red-800',
  };

  return (
    <div data-testid="crm-messages">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h3 className="font-semibold text-slate-800 mb-4">Historico de Mensagens</h3>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">Cliente</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-left">Conteudo</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-left">Data</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">Carregando...</td></tr>
                ) : messages.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">Nenhuma mensagem enviada</td></tr>
                ) : messages.map((m, i) => (
                  <tr key={m.id} className="border-t hover:bg-slate-50" data-testid={`message-row-${i}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{m.customer_name}</p>
                      <p className="text-xs text-slate-400">{m.customer_phone}</p>
                    </td>
                    <td className="px-4 py-3 text-xs">{m.message_type}</td>
                    <td className="px-4 py-3 text-xs max-w-xs truncate">{m.content}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${statusColors[m.status] || ''}`}>{m.status}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(m.created_at).toLocaleString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-slate-800 mb-4">Templates</h3>
          <div className="space-y-3">
            {templates.map((t, i) => (
              <div key={t.id} className="bg-white rounded-xl border p-4 shadow-sm" data-testid={`template-card-${i}`}>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium text-sm">{t.name}</h4>
                  <span className="text-xs bg-slate-100 px-2 py-1 rounded">{t.message_type}</span>
                </div>
                <p className="text-xs text-slate-500">{t.content_template}</p>
              </div>
            ))}
            {templates.length === 0 && <p className="text-sm text-slate-400">Nenhum template</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== CANCELLATIONS TAB =====================
function CancellationsTab() {
  const [cancellations, setCancellations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        crmApi.cancellations(days),
        crmApi.cancellationSummary(days)
      ]);
      setCancellations(listRes.data);
      setSummary(summaryRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="crm-cancellations">
      <div className="flex items-center gap-3 mb-6">
        <select data-testid="cancel-period" value={days} onChange={e => setDays(Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm">
          <option value={7}>7 dias</option>
          <option value={30}>30 dias</option>
          <option value={90}>90 dias</option>
        </select>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border p-4 shadow-sm text-center">
            <p className="text-xs text-slate-500">Total Cancelamentos</p>
            <p className="text-2xl font-bold text-red-600">{summary.total_cancellations}</p>
          </div>
          <div className="bg-white rounded-xl border p-4 shadow-sm text-center">
            <p className="text-xs text-slate-500">Impacto Financeiro</p>
            <p className="text-2xl font-bold text-red-600">R$ {summary.total_impact.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl border p-4 shadow-sm">
            <p className="text-xs text-slate-500 mb-2">Por Motivo</p>
            {summary.by_reason.length === 0 ? (
              <p className="text-sm text-slate-400">Sem dados</p>
            ) : summary.by_reason.map((r, i) => (
              <div key={i} className="flex justify-between text-xs py-0.5">
                <span>{r.reason}</span>
                <span className="font-medium">{r.count}</span>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border p-4 shadow-sm">
            <p className="text-xs text-slate-500 mb-2">Por Canal</p>
            {summary.by_channel.length === 0 ? (
              <p className="text-sm text-slate-400">Sem dados</p>
            ) : summary.by_channel.map((c, i) => (
              <div key={i} className="flex justify-between text-xs py-0.5">
                <span>{c.origin}</span>
                <span className="font-medium">{c.count} - R$ {c.impact.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Pedido</th>
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-left">Canal</th>
              <th className="px-4 py-3 text-left">Motivo</th>
              <th className="px-4 py-3 text-right">Impacto R$</th>
              <th className="px-4 py-3 text-center">Estoque</th>
              <th className="px-4 py-3 text-center">Fiscal</th>
              <th className="px-4 py-3 text-left">Data</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400">Carregando...</td></tr>
            ) : cancellations.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-slate-400">Nenhum cancelamento no periodo</td></tr>
            ) : cancellations.map((c, i) => (
              <tr key={c.id} className="border-t" data-testid={`cancel-row-${i}`}>
                <td className="px-4 py-3 font-mono text-xs">{c.order_id.substring(0, 8)}...</td>
                <td className="px-4 py-3">{c.customer_name || 'N/A'}</td>
                <td className="px-4 py-3 text-xs">{c.order_origin}</td>
                <td className="px-4 py-3 text-xs">{c.reason_code}</td>
                <td className="px-4 py-3 text-right text-red-600 font-medium">R$ {c.financial_impact.toFixed(2)}</td>
                <td className="px-4 py-3 text-center">{c.stock_reversed ? <span className="text-green-600 text-xs">Revertido</span> : <span className="text-slate-400 text-xs">N/A</span>}</td>
                <td className="px-4 py-3 text-center">{c.fiscal_cancelled ? <span className="text-green-600 text-xs">Cancelado</span> : <span className="text-slate-400 text-xs">N/A</span>}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(c.created_at).toLocaleString('pt-BR')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===================== MAIN CRM PAGE =====================
const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
  { id: 'customers', label: 'Clientes', icon: Users },
  { id: 'segments', label: 'Segmentos', icon: Tag },
  { id: 'messages', label: 'Mensagens', icon: MessageSquare },
  { id: 'cancellations', label: 'Cancelamentos', icon: XCircle },
];

export function CRMPage() {
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto" data-testid="crm-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">CRM</h1>
          <p className="text-sm text-slate-500">Gestao de relacionamento com clientes</p>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`crm-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#FF6B6B] text-white shadow-md'
                    : 'bg-white text-slate-600 border hover:bg-slate-50'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'dashboard' && <DashboardTab />}
        {activeTab === 'customers' && <CustomersTab />}
        {activeTab === 'segments' && <SegmentsTab />}
        {activeTab === 'messages' && <MessagesTab />}
        {activeTab === 'cancellations' && <CancellationsTab />}
      </div>
    </MainLayout>
  );
}
